import React from 'react';
import { Section } from './ui/Section';
import { motion } from 'framer-motion';
import { Sun, Sunset, Moon, Calendar, Clock } from 'lucide-react';
export function SolutionSection() {
  return <div className="bg-[#3D2B1F] text-[#FAF6F0] py-20 relative overflow-hidden" data-id="element-147">
      {/* Background texture/noise could go here */}
      <div className="absolute top-0 right-0 w-96 h-96 bg-[#B8845C] rounded-full blur-[150px] opacity-20" data-id="element-148" />

      <Section className="relative z-10" data-id="element-149">
        <div className="text-center mb-16" data-id="element-150">
          <span className="text-[#B8845C] font-serif italic text-xl tracking-wider uppercase mb-4 block" data-id="element-151">
            Presentando
          </span>
          <h2 className="text-4xl md:text-6xl font-bold mb-6 text-[#FAF6F0]" data-id="element-152">
            Tríada 3×7 (el sistema)
          </h2>
          <p className="text-xl md:text-2xl text-[#FAF6F0]/80 max-w-2xl mx-auto" data-id="element-153">
            Este no es un libro para “superar el duelo”. Es un sistema para{' '}
            <strong className="text-[#B8845C]" data-id="element-154">
              hacer tus tardes vivibles
            </strong>{' '}
            y recuperar estabilidad.
          </p>
        </div>

        <div className="grid md:grid-cols-2 gap-12 items-center mb-20" data-id="element-155">
          <div className="space-y-8" data-id="element-156">
            <h3 className="text-3xl font-serif mb-6 text-[#D4A89A]" data-id="element-157">
              Tríada 3×7 significa:
            </h3>

            <ul className="space-y-6" data-id="element-158">
              <motion.li initial={{
              opacity: 0,
              x: -20
            }} whileInView={{
              opacity: 1,
              x: 0
            }} viewport={{
              once: true
            }} className="flex items-start gap-4" data-id="element-159">
                <div className="bg-[#B8845C]/20 p-3 rounded-full" data-id="element-160">
                  <Clock className="w-6 h-6 text-[#B8845C]" data-id="element-161" />
                </div>
                <div data-id="element-162">
                  <strong className="text-xl block text-[#FAF6F0]" data-id="element-163">
                    3 momentos fijos al día
                  </strong>
                  <span className="text-[#FAF6F0]/70 text-lg" data-id="element-164">
                    Mañana / Tarde / Noche
                  </span>
                </div>
              </motion.li>

              <motion.li initial={{
              opacity: 0,
              x: -20
            }} whileInView={{
              opacity: 1,
              x: 0
            }} viewport={{
              once: true
            }} transition={{
              delay: 0.2
            }} className="flex items-start gap-4" data-id="element-165">
                <div className="bg-[#B8845C]/20 p-3 rounded-full" data-id="element-166">
                  <Sun className="w-6 h-6 text-[#B8845C]" data-id="element-167" />
                </div>
                <div data-id="element-168">
                  <strong className="text-xl block text-[#FAF6F0]" data-id="element-169">
                    1 micro-acción en cada momento
                  </strong>
                  <span className="text-[#FAF6F0]/70 text-lg" data-id="element-170">
                    Solo 5–15 minutos. Sin esfuerzo heroico.
                  </span>
                </div>
              </motion.li>

              <motion.li initial={{
              opacity: 0,
              x: -20
            }} whileInView={{
              opacity: 1,
              x: 0
            }} viewport={{
              once: true
            }} transition={{
              delay: 0.4
            }} className="flex items-start gap-4" data-id="element-171">
                <div className="bg-[#B8845C]/20 p-3 rounded-full" data-id="element-172">
                  <Calendar className="w-6 h-6 text-[#B8845C]" data-id="element-173" />
                </div>
                <div data-id="element-174">
                  <strong className="text-xl block text-[#FAF6F0]" data-id="element-175">
                    Repetido por 21 días
                  </strong>
                  <span className="text-[#FAF6F0]/70 text-lg" data-id="element-176">
                    7 días de base, ajustados por 3 semanas.
                  </span>
                </div>
              </motion.li>
            </ul>
          </div>

          <div className="bg-[#FAF6F0]/5 p-8 rounded-2xl border border-[#FAF6F0]/10" data-id="element-177">
            <p className="text-2xl font-serif italic text-center leading-relaxed" data-id="element-178">
              "¿La diferencia? No depende de motivación. Depende de repetición.
              Y eso, cuando estás frágil, es todo."
            </p>
          </div>
        </div>

        <div className="bg-[#FAF6F0] text-[#3D2B1F] rounded-2xl p-8 md:p-12 shadow-2xl" data-id="element-179">
          <h3 className="text-2xl md:text-3xl font-bold mb-6 text-center" data-id="element-180">
            Por qué este plan funciona cuando “ser fuerte” falla
          </h3>
          <p className="text-lg md:text-xl mb-8 text-center max-w-3xl mx-auto" data-id="element-181">
            Porque ataca el verdadero problema oculto:{' '}
            <strong className="text-[#B8845C]" data-id="element-182">
              El silencio se vuelve peligroso cuando llega sin estructura.
            </strong>
          </p>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-4 text-center" data-id="element-183">
            <div className="p-4 bg-[#FAF6F0] border border-[#D4A89A]/30 rounded-lg" data-id="element-184">
              <Sun className="w-8 h-8 mx-auto text-[#B8845C] mb-3" data-id="element-185" />
              <p className="font-medium" data-id="element-186">Qué hacer en la mañana</p>
            </div>
            <div className="p-4 bg-[#FAF6F0] border border-[#D4A89A]/30 rounded-lg" data-id="element-187">
              <Sunset className="w-8 h-8 mx-auto text-[#B8845C] mb-3" data-id="element-188" />
              <p className="font-medium" data-id="element-189">Qué hacer cuando empieza la tarde</p>
            </div>
            <div className="p-4 bg-[#FAF6F0] border border-[#D4A89A]/30 rounded-lg" data-id="element-190">
              <Moon className="w-8 h-8 mx-auto text-[#B8845C] mb-3" data-id="element-191" />
              <p className="font-medium" data-id="element-192">Qué hacer antes de dormir</p>
            </div>
          </div>

          <p className="mt-8 text-lg text-center italic text-[#3D2B1F]/80" data-id="element-193">
            …tu sistema nervioso baja la guardia. No porque “ya no te duela”.
            Sino porque ya no te sientes a merced del golpe.
          </p>
        </div>
      </Section>
    </div>;
}